import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, TextField, Button,
  Chip, Collapse, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, CircularProgress,
  Divider, Tooltip, Stack, ToggleButton, ToggleButtonGroup,
  Tabs, Tab, AppBar, Toolbar
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  ExpandMore, ExpandLess, Search, Refresh, CompareArrows,
  CheckCircle, Warning, Error as ErrorIcon, ArrowBack,
  ContentCopy, FilterList, Timeline, UploadFile
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../api/authService';
import { outboxLogService, negozioService, dipendenteService, gdvImportTurniService } from '../api/services';
import {
  OutboxLogEntry, OutboxConfruntaEntry, TcVsGdvEntry, Negozio, Dipendente
} from '../types/types';

// ─── Utility ────────────────────────────────────────────────────────────────

const OP_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  INS: { label: 'INS', color: 'success' },
  UPD: { label: 'UPD', color: 'warning' },
  DEL: { label: 'DEL', color: 'error' },
};

const STATUS_CONFIG: Record<string, { color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  SENT:    { color: 'success' },
  PENDING: { color: 'warning' },
  SENDING: { color: 'info' },
  FAILED:  { color: 'error' },
};

function opChip(op?: string) {
  if (!op) return <Chip label="—" size="small" />;
  const cfg = OP_CONFIG[op.toUpperCase()] ?? { label: op, color: 'default' };
  return <Chip label={cfg.label} size="small" color={cfg.color} sx={{ fontWeight: 'bold', minWidth: 48 }} />;
}

function statusChip(status?: string) {
  if (!status) return <Chip label="—" size="small" />;
  const cfg = STATUS_CONFIG[status.toUpperCase()] ?? { color: 'default' };
  return <Chip label={status} size="small" color={cfg.color} />;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// ─── Sub-componente: riga evento timeline ────────────────────────────────────

const TimelineRow: React.FC<{ ev: OutboxLogEntry; idx: number }> = ({ ev, idx }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyJson = () => {
    if (ev.payloadJsonRaw) {
      navigator.clipboard.writeText(ev.payloadJsonRaw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // Rileva anomalie: stesso dipendente+giorno con multipli eventi?
  const bgColor = ev.status === 'FAILED' ? 'rgba(211,47,47,0.06)' :
    ev.op?.toUpperCase() === 'DEL' ? 'rgba(211,47,47,0.04)' :
    ev.op?.toUpperCase() === 'INS' ? 'rgba(46,125,50,0.04)' :
    ev.op?.toUpperCase() === 'UPD' ? 'rgba(237,108,2,0.04)' : 'transparent';

  return (
    <>
      <TableRow
        sx={{ backgroundColor: bgColor, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
        onClick={() => setOpen(o => !o)}
      >
        {/* Timeline connector */}
        <TableCell sx={{ width: 40, p: 0.5, textAlign: 'center', verticalAlign: 'middle' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{
              width: 12, height: 12, borderRadius: '50%',
              backgroundColor:
                ev.status === 'SENT' ? '#2e7d32' :
                ev.status === 'FAILED' ? '#d32f2f' :
                ev.status === 'SENDING' ? '#0288d1' : '#ed6c02',
              border: '2px solid white',
              boxShadow: '0 0 0 2px currentColor',
              boxShadowColor: 'inherit',
              zIndex: 1
            }} />
          </Box>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{ev.createdAt}</TableCell>
        <TableCell>{opChip(ev.op)}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{ev.codicePersonale ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ev.nomeDipendente ?? '—'}
        </TableCell>
        {/* Negozio: mostra codClifor (normalizzato) + nomeNegozio se disponibile */}
        <TableCell sx={{ fontSize: 12 }}>
          {ev.codClifor ?? '—'}{ev.nomeNegozio ? ` (${ev.nomeNegozio})` : ''}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{ev.dataDelGiorno?.slice(0, 10) ?? '—'}</TableCell>
        {/* turno: dal backend è già il nome testuale (Mattina/Pomeriggio/Intermezzo) */}
        <TableCell sx={{ fontSize: 12 }}>{ev.turno ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{ev.oraConsInizio ?? '—'} – {ev.oraConsFine ?? '—'}</TableCell>
        <TableCell>{statusChip(ev.status)}</TableCell>
        <TableCell sx={{ p: 0.5 }}>
          <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Dettaglio espanso */}
      <TableRow>
        <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: '#fafafa', borderLeft: '4px solid #1976d2' }}>
              <Stack direction="row" spacing={2} flexWrap="wrap" mb={1}>
                <Typography variant="caption"><strong>ID:</strong> {ev.id}</Typography>
                <Typography variant="caption"><strong>Tentativi:</strong> {ev.attempts}</Typography>
                <Typography variant="caption"><strong>Utente Modifica:</strong> {ev.utenteModifica ?? '—'}</Typography>
                <Typography variant="caption"><strong>Codice Tipo Orario:</strong> {ev.codiceTipoOrario ?? '—'}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  <strong>Idempotency Key:</strong> {ev.idempotencyKey ?? '—'}
                </Typography>
              </Stack>

              {ev.lastError && (
                <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                  <Typography variant="caption"><strong>Errore:</strong> {ev.lastError}</Typography>
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Payload JSON:</Typography>
                <Tooltip title={copied ? 'Copiato!' : 'Copia JSON'}>
                  <IconButton size="small" onClick={copyJson}>
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                component="pre"
                sx={{
                  fontSize: 11, maxHeight: 200, overflow: 'auto',
                  backgroundColor: '#1e1e1e', color: '#d4d4d4',
                  borderRadius: 1, p: 1, mt: 0.5, fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                }}
              >
                {ev.payloadJsonRaw
                  ? (() => { try { return JSON.stringify(JSON.parse(ev.payloadJsonRaw), null, 2); } catch { return ev.payloadJsonRaw; } })()
                  : '—'}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Sub-componente: riga confronto ─────────────────────────────────────────

const ConfruntaRow: React.FC<{ item: OutboxConfruntaEntry }> = ({ item }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: item.allineato ? 'rgba(46,125,50,0.04)' : 'rgba(211,47,47,0.06)',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
        }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell sx={{ width: 40, textAlign: 'center' }}>
          {item.allineato
            ? <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
            : <Warning sx={{ color: '#d32f2f', fontSize: 20 }} />}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.dataDelGiorno ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.codicePersonale ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.nomeDipendente ?? '—'}</TableCell>
        {/* Negozio: codice normalizzato + nome se disponibile */}
        <TableCell sx={{ fontSize: 12 }}>
          {item.codClifor ?? '—'}{item.nomeNegozio ? ` (${item.nomeNegozio})` : ''}
        </TableCell>
        <TableCell>{opChip(item.outboxOp)}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{statusChip(item.outboxStatus)}</TableCell>
        <TableCell sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.esito ?? '—'}
        </TableCell>
        <TableCell sx={{ p: 0.5 }}>
          <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: '#fafafa', borderLeft: `4px solid ${item.allineato ? '#2e7d32' : '#d32f2f'}` }}>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    📦 Outbox (ultimo inviato)
                  </Typography>
                  <Typography variant="caption" display="block">Op: {item.outboxOp ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Prev Inizio: {item.outboxOraPrevInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Prev Fine: {item.outboxOraPrevFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Inizio: {item.outboxOraConsInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Fine: {item.outboxOraConsFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Turno: {item.outboxTurno ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Tipo Orario: {item.outboxCodiceTipoOrario ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Inviato: {item.outboxCreatedAt ?? '—'}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    🗃️ turni_creati (stato attuale DB)
                  </Typography>
                  <Typography variant="caption" display="block">Prev Inizio: {item.tcOraPrevInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Prev Fine: {item.tcOraPrevFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Inizio: {item.tcOraConsInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Fine: {item.tcOraConsFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Turno: {item.tcTurno ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Tipo Orario: {item.tcCodiceTipoOrario ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Utente: {item.tcUtenteModifica ?? '—'}</Typography>
                </Box>
              </Stack>

              {item.discrepanze && item.discrepanze.length > 0 && (
                <Box mt={1.5}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    ⚠️ Discrepanze rilevate:
                  </Typography>
                  {item.discrepanze.map((d, i) => (
                    <Alert key={i} severity="error" sx={{ py: 0, mt: 0.5 }}>
                      <Typography variant="caption">{d}</Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Sub-componente: riga confronto TC vs GDV ────────────────────────────────

const GdvConfruntaRow: React.FC<{ item: TcVsGdvEntry }> = ({ item }) => {
  const [open, setOpen] = useState(false);

  const esitoColor = item.allineato
    ? 'rgba(46,125,50,0.04)'
    : item.esito?.startsWith('INFO')
      ? 'rgba(2,136,209,0.05)'
      : 'rgba(211,47,47,0.07)';

  return (
    <>
      <TableRow
        sx={{ backgroundColor: esitoColor, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell sx={{ width: 36, textAlign: 'center' }}>
          {item.allineato
            ? <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
            : item.esito?.startsWith('INFO')
              ? <CheckCircle sx={{ color: '#0288d1', fontSize: 20 }} />
              : <Warning sx={{ color: '#d32f2f', fontSize: 20 }} />}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.dataDelGiorno ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.codicePersonale ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.nomeDipendente ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.negozio ?? item.codClifor ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>
          {item.gdvTurno ?? '—'}
          {item.gdvTurnoRaw ? <Typography component="span" variant="caption" color="text.secondary"> ({item.gdvTurnoRaw})</Typography> : null}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.tcTurno ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12 }}>
          {item.gdvCodiceTipoOrario ?? '—'}
          {item.gdvCtoRaw ? <Typography component="span" variant="caption" color="text.secondary"> ({item.gdvCtoRaw})</Typography> : null}
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>{item.tcCodiceTipoOrario ?? '—'}</TableCell>
        <TableCell sx={{ fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.esito ?? '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: '#fafafa', borderLeft: `4px solid ${item.allineato ? '#2e7d32' : '#d32f2f'}` }}>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    🗃️ turni_creati (TC)
                  </Typography>
                  <Typography variant="caption" display="block">Turno: {item.tcTurno ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Tipo Orario: {item.tcCodiceTipoOrario ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Prev Inizio: {item.tcOraPrevInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Prev Fine: {item.tcOraPrevFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Inizio: {item.tcOraConsInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Fine: {item.tcOraConsFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Utente: {item.tcUtenteModifica ?? '—'}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                    📋 GDV CSV importato
                  </Typography>
                  <Typography variant="caption" display="block">Turno: {item.gdvTurno ?? '—'} (raw: {item.gdvTurnoRaw ?? '—'})</Typography>
                  <Typography variant="caption" display="block">Tipo Orario: {item.gdvCodiceTipoOrario ?? '—'} (raw: {item.gdvCtoRaw ?? '—'})</Typography>
                  <Typography variant="caption" display="block">Prev Inizio: {item.gdvOraPrevInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Prev Fine: {item.gdvOraPrevFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Inizio: {item.gdvOraConsInizio ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Cons Fine: {item.gdvOraConsFine ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Utente: {item.gdvUtenteModifica ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Flag Approvazione: {item.gdvFlagApprovazione ?? '—'}</Typography>
                  <Typography variant="caption" display="block">Annullato: {item.gdvAnnullato ?? '—'}</Typography>
                </Box>
              </Stack>

              {item.discrepanze && item.discrepanze.length > 0 && (
                <Box mt={1.5}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    ⚠️ Discrepanze rilevate:
                  </Typography>
                  {item.discrepanze.map((d, i) => (
                    <Alert key={i} severity="error" sx={{ py: 0, mt: 0.5 }}>
                      <Typography variant="caption">{d}</Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Componente principale ───────────────────────────────────────────────────

const GdvOutboxLog: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isAdmin = !!user && user.ruolo === 'Admin';

  // ── Stato filtri ────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<0 | 1 | 2>(0);

  // Negozi (per dropdown)
  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [negozioSelezionato, setNegozioSelezionato] = useState<Negozio | null>(null);

  // Dipendenti del negozio selezionato (per dropdown)
  const [dipendenti, setDipendenti] = useState<Dipendente[]>([]);
  const [dipendenteSelezionato, setDipendenteSelezionato] = useState<Dipendente | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    negozioService.getAll()
      .then(data => setNegozi(data))
      .catch(() => setNegozi([]));
  }, [isAdmin]);

  // Carica i dipendenti ogni volta che cambia il negozio selezionato
  useEffect(() => {
    if (!negozioSelezionato) {
      setDipendenti([]);
      setDipendenteSelezionato(null);
      setCodicePersonale('');
      return;
    }
    dipendenteService.getByNegozio(negozioSelezionato.neg)
      .then(data => setDipendenti(data))
      .catch(() => setDipendenti([]));
    // Resetta il dipendente selezionato al cambio negozio
    setDipendenteSelezionato(null);
    setCodicePersonale('');
  }, [negozioSelezionato]);

  // Filtri log
  const [dataInizio, setDataInizio] = useState(daysAgo(7));
  const [dataFine, setDataFine] = useState(today());
  const [codicePersonale, setCodicePersonale] = useState('');
  const [codClifor, setCodClifor] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<string[]>([]);

  // Dati log
  const [logData, setLogData] = useState<OutboxLogEntry[]>([]);
  const [logTotale, setLogTotale] = useState(0);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  // Dati confronto
  const [confrontaData, setConfruntaData] = useState<OutboxConfruntaEntry[]>([]);
  const [confrontaSummary, setConfruntaSummary] = useState<{ totale: number; anomalie: number; allineati: number } | null>(null);
  const [confrontaLoading, setConfruntaLoading] = useState(false);
  const [confrontaError, setConfruntaError] = useState<string | null>(null);

  // Dati confronto TC vs GDV (tab 2)
  const [gdvConfruntaData, setGdvConfruntaData] = useState<TcVsGdvEntry[]>([]);
  const [gdvConfruntaSummary, setGdvConfruntaSummary] = useState<{
    totale: number; anomalie: number; allineati: number; soloInGdv: number; soloInTc: number;
  } | null>(null);
  const [gdvConfruntaLoading, setGdvConfruntaLoading] = useState(false);
  const [gdvConfruntaError, setGdvConfruntaError] = useState<string | null>(null);

  // ── Import CSV ──────────────────────────────────────────────────────────────
  const importFileRef = React.useRef<HTMLInputElement>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    righeImportate: number;
    righeScartate: number;
    rigeTotaliCsv: number;
    errori: string[];
  } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Resetta input per permettere reimport dello stesso file
    e.target.value = '';
    setImportLoading(true);
    setImportResult(null);
    setImportError(null);
    try {
      const res = await gdvImportTurniService.uploadCsv(file);
      setImportResult(res);
    } catch (err: any) {
      setImportError(
        err?.response?.data?.errore ?? err?.message ?? 'Errore durante l\'import'
      );
    } finally {
      setImportLoading(false);
    }
  };

  // ── Contatori status ─────────────────────────────────────────────────────────
  const contaPerStatus = (s: string) => logData.filter(e => e.status === s).length;

  // ── Handlers (useCallback prima dell'early return per rispettare Rules of Hooks) ──
  const cercaLog = useCallback(async () => {
    setLogLoading(true);
    setLogError(null);
    try {
      const status = statusFiltro.length === 1 ? statusFiltro[0] : undefined;
      const res = await outboxLogService.getLog({
        dataInizio, dataFine, codicePersonale: codicePersonale || undefined,
        codClifor: codClifor || undefined, status
      });
      setLogData(res.eventi ?? []);
      setLogTotale(res.totale ?? 0);
    } catch (e: any) {
      setLogError(e?.response?.data?.error ?? e?.message ?? 'Errore sconosciuto');
    } finally {
      setLogLoading(false);
    }
  }, [dataInizio, dataFine, codicePersonale, codClifor, statusFiltro]);

  const cercaConfronta = useCallback(async () => {
    setConfruntaLoading(true);
    setConfruntaError(null);
    try {
      const res = await outboxLogService.confronta({
        dataInizio, dataFine,
        codicePersonale: codicePersonale || undefined,
        codClifor: codClifor || undefined
      });
      setConfruntaData(res.risultati ?? []);
      setConfruntaSummary({
        totale: res.totaleDipendentiGiorni,
        anomalie: res.anomalie,
        allineati: res.allineati
      });
    } catch (e: any) {
      setConfruntaError(e?.response?.data?.error ?? e?.message ?? 'Errore sconosciuto');
    } finally {
      setConfruntaLoading(false);
    }
  }, [dataInizio, dataFine, codicePersonale, codClifor]);

  const cercaConfruntaGdv = useCallback(async () => {
    setGdvConfruntaLoading(true);
    setGdvConfruntaError(null);
    try {
      const res = await outboxLogService.confrontaGdv({
        dataInizio, dataFine,
        codicePersonale: codicePersonale || undefined,
        codClifor: codClifor || undefined
      });
      setGdvConfruntaData(res.risultati ?? []);
      setGdvConfruntaSummary({
        totale: res.totale,
        anomalie: res.anomalie,
        allineati: res.allineati,
        soloInGdv: res.soloInGdv,
        soloInTc: res.soloInTc
      });
    } catch (e: any) {
      setGdvConfruntaError(e?.response?.data?.error ?? e?.message ?? 'Errore sconosciuto');
    } finally {
      setGdvConfruntaLoading(false);
    }
  }, [dataInizio, dataFine, codicePersonale, codClifor]);

  // ── Protezione: solo Admin (dopo TUTTI gli hook) ──────────────────────────
  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />
        <Typography variant="h5" gutterBottom>Accesso negato</Typography>
        <Typography variant="body2" color="text.secondary">
          Questa pagina è riservata agli amministratori.
        </Typography>
        <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate('/dashboard')}>
          Torna alla Dashboard
        </Button>
      </Container>
    );
  }

  const handleTabChange = (_: any, newVal: number) => {
    setTab(newVal as 0 | 1 | 2);
  };

  // ── Rendering filtri (comune ai due tab) ─────────────────────────────────────
  const renderFiltri = () => (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <FilterList fontSize="small" />
        <Typography variant="subtitle2" fontWeight="bold">Filtri</Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
        <TextField
          label="Data inizio" type="date" size="small"
          value={dataInizio}
          onChange={e => setDataInizio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Data fine" type="date" size="small"
          value={dataFine}
          onChange={e => setDataFine(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        {/* Dropdown negozio basato sulla tabella negozi — invia il CODCLIFOR corrispondente */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 240 }}
          options={negozi}
          value={negozioSelezionato}
          onChange={(_, newVal) => {
            setNegozioSelezionato(newVal);
            setCodClifor(newVal?.codClifor ?? '');
          }}
          getOptionLabel={(n: Negozio) => `${n.neg} - ${n.negozio} (${n.codClifor})`}
          isOptionEqualToValue={(a: Negozio, b: Negozio) => a.neg === b.neg}
          renderInput={params => (
            <TextField
              {...params}
              label="Negozio"
              placeholder="Seleziona o cerca..."
            />
          )}
          clearOnEscape
          noOptionsText="Nessun negozio trovato"
        />
        {/* Dropdown dipendenti — abilitato solo dopo aver selezionato un negozio */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={dipendenti}
          value={dipendenteSelezionato}
          disabled={!negozioSelezionato}
          onChange={(_, newVal) => {
            setDipendenteSelezionato(newVal);
            setCodicePersonale(newVal?.codicePersonale ?? '');
          }}
          getOptionLabel={(d: Dipendente) => `${d.nome} (${d.codicePersonale})`}
          isOptionEqualToValue={(a: Dipendente, b: Dipendente) => a.codicePersonale === b.codicePersonale}
          renderInput={params => (
            <TextField
              {...params}
              label="Dipendente"
              placeholder={negozioSelezionato ? 'Tutti i dipendenti' : 'Prima seleziona un negozio'}
            />
          )}
          clearOnEscape
          noOptionsText="Nessun dipendente trovato"
        />
        {tab === 0 && (
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
              Status (opzionale)
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={statusFiltro}
              onChange={(_, newVal) => setStatusFiltro(newVal)}
              aria-label="filtro status"
            >
              {['SENT', 'PENDING', 'SENDING', 'FAILED'].map(s => (
                <ToggleButton key={s} value={s} sx={{ fontSize: 11, py: 0.5, px: 1 }}>
                  {s}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
      </Stack>
      <Stack direction="row" spacing={1} mt={2}>
        <Button
          variant="contained"
          startIcon={tab === 0 ? <Search /> : <CompareArrows />}
          onClick={tab === 0 ? cercaLog : tab === 1 ? cercaConfronta : cercaConfruntaGdv}
          disabled={tab === 0 ? logLoading : tab === 1 ? confrontaLoading : gdvConfruntaLoading}
        >
          {tab === 0 ? 'Cerca eventi' : tab === 1 ? 'Analizza allineamento' : 'Confronta TC vs GDV'}
        </Button>
        {tab === 0 && logData.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={cercaLog}
            disabled={logLoading}
          >
            Aggiorna
          </Button>
        )}
      </Stack>
    </Paper>
  );

  // ── Tab 0: Timeline log ────────────────────────────────────────────────────
  const renderLog = () => (
    <>
      {logError && <Alert severity="error" sx={{ mb: 2 }}>{logError}</Alert>}
      {logLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!logLoading && logData.length > 0 && (
        <>
          {/* Riepilogo contatori */}
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            <Chip label={`Totale: ${logTotale}`} color="default" />
            <Chip label={`SENT: ${contaPerStatus('SENT')}`} color="success" />
            <Chip label={`PENDING: ${contaPerStatus('PENDING')}`} color="warning" />
            <Chip label={`SENDING: ${contaPerStatus('SENDING')}`} color="info" />
            <Chip label={`FAILED: ${contaPerStatus('FAILED')}`} color="error" />
          </Stack>

          {/* Legenda OP */}
          <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">Operazioni:</Typography>
            <Chip label="INS" size="small" color="success" />
            <Chip label="UPD" size="small" color="warning" />
            <Chip label="DEL" size="small" color="error" />
          </Stack>

          <TableContainer component={Paper} elevation={1}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                  <TableCell />
                  <TableCell>Data/ora evento</TableCell>
                  <TableCell>Op</TableCell>
                  <TableCell>Cod. Personale</TableCell>
                  <TableCell>Dipendente</TableCell>
                  <TableCell>Negozio</TableCell>
                  <TableCell>Data turno</TableCell>
                  <TableCell>Turno</TableCell>
                  <TableCell>Ore consuntivo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {logData.map((ev, idx) => (
                  <TimelineRow key={ev.id} ev={ev} idx={idx} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {!logLoading && logData.length === 0 && logError === null && (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Timeline sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>Nessun risultato. Imposta i filtri e premi "Cerca eventi".</Typography>
        </Paper>
      )}
    </>
  );

  // ── Tab 1: Confronto ───────────────────────────────────────────────────────
  const renderConfronta = () => (
    <>
      {confrontaError && <Alert severity="error" sx={{ mb: 2 }}>{confrontaError}</Alert>}
      {confrontaLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!confrontaLoading && confrontaSummary && (
        <>
          {/* Riepilogo */}
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            <Chip label={`Totale coppie dipendente+giorno: ${confrontaSummary.totale}`} color="default" />
            <Chip
              icon={<CheckCircle />}
              label={`Allineati: ${confrontaSummary.allineati}`}
              color="success"
            />
            <Chip
              icon={<Warning />}
              label={`Anomalie: ${confrontaSummary.anomalie}`}
              color={confrontaSummary.anomalie > 0 ? 'error' : 'success'}
            />
          </Stack>

          {confrontaSummary.anomalie > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{confrontaSummary.anomalie}</strong> discrepanza/e trovata/e tra l'ultimo evento outbox e turni_creati.
              Le righe in rosso indicano dati non allineati.
            </Alert>
          )}
          {confrontaSummary.anomalie === 0 && confrontaSummary.totale > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Tutti i dati outbox sono allineati con turni_creati nel periodo selezionato. ✓
            </Alert>
          )}

          <TableContainer component={Paper} elevation={1}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                  <TableCell />
                  <TableCell>Data turno</TableCell>
                  <TableCell>Cod. Personale</TableCell>
                  <TableCell>Dipendente</TableCell>
                  <TableCell>Negozio</TableCell>
                  <TableCell>Op outbox</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Esito</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {confrontaData.map((item, idx) => (
                  <ConfruntaRow key={`${item.codicePersonale}-${item.dataDelGiorno}-${idx}`} item={item} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {!confrontaLoading && !confrontaSummary && confrontaError === null && (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <CompareArrows sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>
            Imposta i filtri e premi "Analizza allineamento" per confrontare l'outbox con turni_creati.
          </Typography>
          <Typography variant="caption" display="block" mt={1}>
            Per ogni dipendente+giorno viene preso l'ultimo evento outbox e confrontato con lo stato corrente in turni_creati.
          </Typography>
        </Paper>
      )}
    </>
  );

  // ── Tab 2: Confronto TC vs GDV ─────────────────────────────────────────────
  const renderConfruntaGdv = () => (
    <>
      {gdvConfruntaError && <Alert severity="error" sx={{ mb: 2 }}>{gdvConfruntaError}</Alert>}
      {gdvConfruntaLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!gdvConfruntaLoading && gdvConfruntaSummary && (
        <>
          {/* Riepilogo badge */}
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            <Chip label={`Totale righe: ${gdvConfruntaSummary.totale}`} color="default" />
            <Chip icon={<CheckCircle />} label={`Allineati: ${gdvConfruntaSummary.allineati}`} color="success" />
            <Chip
              icon={<Warning />}
              label={`Anomalie/Discrepanze: ${gdvConfruntaSummary.anomalie}`}
              color={gdvConfruntaSummary.anomalie > 0 ? 'error' : 'success'}
            />
            <Chip label={`Solo GDV: ${gdvConfruntaSummary.soloInGdv}`} color="warning" variant="outlined" />
            <Chip label={`Solo TC: ${gdvConfruntaSummary.soloInTc}`} color="info" variant="outlined" />
          </Stack>

          {gdvConfruntaSummary.anomalie > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{gdvConfruntaSummary.anomalie}</strong> discrepanza/e trovata/e tra turni_creati e il CSV GDV importato.
              Le righe in rosso indicano dati non allineati.
            </Alert>
          )}
          {gdvConfruntaSummary.anomalie === 0 && gdvConfruntaSummary.totale > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Tutti i record confrontati sono allineati tra turni_creati e il CSV GDV. ✓
            </Alert>
          )}

          <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                  <TableCell sx={{ width: 36 }} />
                  <TableCell>Data</TableCell>
                  <TableCell>Cod. Personale</TableCell>
                  <TableCell>Dipendente</TableCell>
                  <TableCell>Negozio</TableCell>
                  <TableCell>Turno GDV</TableCell>
                  <TableCell>Turno TC</TableCell>
                  <TableCell>CTO GDV</TableCell>
                  <TableCell>CTO TC</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Esito</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gdvConfruntaData.map((item, idx) => (
                  <GdvConfruntaRow key={`${item.codicePersonale}-${item.dataDelGiorno}-${item.gdvTurno ?? item.tcTurno}-${idx}`} item={item} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {!gdvConfruntaLoading && !gdvConfruntaSummary && gdvConfruntaError === null && (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <CompareArrows sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>
            Imposta i filtri e premi "Confronta TC vs GDV" per analizzare le differenze
            tra turni_creati e il CSV GDV importato.
          </Typography>
          <Typography variant="caption" display="block" mt={1}>
            Richiede che sia stato importato un CSV GDV tramite il pulsante "Import CSV GDV".
            Chiave di confronto: CODICEPERSONALE + DATA + Turno.
          </Typography>
        </Paper>
      )}
    </>
  );

  // ── Render principale ───────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <IconButton edge="start" onClick={() => navigate('/dashboard')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: 16 }}>
            🔍 GDV Outbox — Pannello di Diagnostica
          </Typography>
          <Chip label="Solo Admin" size="small" color="error" variant="outlined" sx={{ mr: 1 }} />

          {/* ── Pulsante Import CSV GDV ── */}
          <input
            ref={importFileRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleImportCsv}
          />
          <Tooltip title="Importa turni GDV da file CSV. Svuota la tabella gdv_import_turni e reinserisce i dati del file.">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={importLoading ? <CircularProgress size={14} /> : <UploadFile />}
                onClick={() => importFileRef.current?.click()}
                disabled={importLoading}
                sx={{ ml: 1, textTransform: 'none', fontSize: 12 }}
              >
                {importLoading ? 'Import...' : 'Import CSV GDV'}
              </Button>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── Feedback import CSV ── */}
        {importError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
            <strong>Errore import:</strong> {importError}
          </Alert>
        )}
        {importResult && (
          <Alert
            severity={importResult.righeScartate > 0 ? 'warning' : 'success'}
            sx={{ mb: 2 }}
            onClose={() => setImportResult(null)}
          >
            <strong>Import completato:</strong>{' '}
            {importResult.righeImportate} righe importate su {importResult.rigeTotaliCsv} totali
            {importResult.righeScartate > 0 && ` (${importResult.righeScartate} scartate)`}.
            {importResult.errori.length > 0 && (
              <Box mt={0.5}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  Righe con errori:
                </Typography>
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {importResult.errori.slice(0, 10).map((e, i) => (
                    <li key={i}><Typography variant="caption">{e}</Typography></li>
                  ))}
                  {importResult.errori.length > 10 && (
                    <li><Typography variant="caption">… e altri {importResult.errori.length - 10} errori</Typography></li>
                  )}
                </ul>
              </Box>
            )}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab
              icon={<Timeline />}
              iconPosition="start"
              label="Timeline eventi outbox"
              value={0}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<CompareArrows />}
              iconPosition="start"
              label="Confronto con turni_creati"
              value={1}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={<CompareArrows />}
              iconPosition="start"
              label="Confronto TC vs GDV"
              value={2}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {renderFiltri()}

        {tab === 0 && renderLog()}
        {tab === 1 && renderConfronta()}
        {tab === 2 && renderConfruntaGdv()}
      </Container>
    </Box>
  );
};

export default GdvOutboxLog;
